import React, { useMemo } from 'react';

const HighlightCard = ({ icon, title, value, sub, accent }) => (
  <div className={`border p-3 bg-[rgba(0,0,0,0.12)] flex flex-col gap-1 ${accent ? 'border-[#d8ceb3]' : 'border-[#658d71]'}`}>
    <span className="text-lg">{icon}</span>
    <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold">{title}</span>
    <span className={`text-sm font-black uppercase leading-tight ${accent ? 'text-[#d8ceb3]' : 'text-white'}`}>{value || '--'}</span>
    {sub && <span className="text-[9px] text-[#a9c9b1] font-bold">{sub}</span>}
  </div>
);

const GameHighlights = ({ allPlays, boxScore, awayAbbr, homeAbbr }) => {
  const highlights = useMemo(() => {
    if (!allPlays?.length && !boxScore) return null;

    const plays = allPlays || [];

    // Find biggest hit
    const homeRuns = plays.filter(p => p.result?.event === 'Home Run');
    const grandSlams = homeRuns.filter(p => p.result?.rbi >= 4);
    const biggestHR = grandSlams[0] || homeRuns[0];

    // Biggest inning
    let biggestInning = null;
    let biggestRuns = 0;
    const inningMap = {};
    plays.forEach(p => {
      const key = `${p.about?.inning}-${p.about?.isTopInning}`;
      if (!inningMap[key]) inningMap[key] = { runs: 0, inning: p.about?.inning, isTop: p.about?.isTopInning };
      inningMap[key].runs += p.result?.rbi || 0;
    });
    Object.values(inningMap).forEach(inn => {
      if (inn.runs > biggestRuns) { biggestRuns = inn.runs; biggestInning = inn; }
    });

    // Top batter
    let topBatter = null;
    let topScore = 0;
    if (boxScore) {
      for (const side of ['away', 'home']) {
        const players = Object.values(boxScore.teams?.[side]?.players || {});
        players.forEach(p => {
          const b = p.stats?.batting;
          if (!b) return;
          const score = (b.hits || 0) * 2 + (b.homeRuns || 0) * 4 + (b.rbi || 0) * 2 + (b.runs || 0);
          if (score > topScore) {
            topScore = score;
            topBatter = { name: p.person?.fullName, b };
          }
        });
      }
    }

    // Top pitcher (most K's)
    let topPitcher = null;
    let topK = 0;
    if (boxScore) {
      for (const side of ['away', 'home']) {
        const players = Object.values(boxScore.teams?.[side]?.players || {});
        players.forEach(p => {
          const pit = p.stats?.pitching;
          if (!pit) return;
          if ((pit.strikeOuts || 0) > topK) {
            topK = pit.strikeOuts;
            topPitcher = { name: p.person?.fullName, pit };
          }
        });
      }
    }

    // Total LOB
    const awayLOB = boxScore?.teams?.away?.teamStats?.batting?.leftOnBase ?? '--';
    const homeLOB = boxScore?.teams?.home?.teamStats?.batting?.leftOnBase ?? '--';

    return { biggestHR, biggestInning, biggestRuns, topBatter, topPitcher, awayLOB, homeLOB };
  }, [allPlays, boxScore]);

  if (!highlights) return (
    <div className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">No highlight data</div>
  );

  const { biggestHR, biggestInning, biggestRuns, topBatter, topPitcher, awayLOB, homeLOB } = highlights;

  const cards = [
    biggestHR && {
      icon: '💣',
      title: biggestHR.result?.rbi >= 4 ? 'Grand Slam' : 'Home Run',
      value: biggestHR.result?.description?.split(' ')[0] + ' ' + (biggestHR.result?.description?.split(' ')[1] || ''),
      sub: `Inn ${biggestHR.about?.inning} · ${biggestHR.result?.rbi} RBI`,
      accent: true,
    },
    biggestInning && biggestRuns > 0 && {
      icon: '🔥',
      title: 'Biggest Inning',
      value: `${biggestRuns} Runs`,
      sub: `${biggestInning.isTop ? '▲' : '▼'} Inning ${biggestInning.inning}`,
      accent: false,
    },
    topBatter && {
      icon: '⚾',
      title: 'Top Batter',
      value: topBatter.name?.split(' ').pop(),
      sub: `${topBatter.b.hits}H ${topBatter.b.rbi || 0}RBI${topBatter.b.homeRuns ? ` ${topBatter.b.homeRuns}HR` : ''}`,
      accent: false,
    },
    topPitcher && {
      icon: '⚡',
      title: 'Top Pitcher',
      value: topPitcher.name?.split(' ').pop(),
      sub: `${topPitcher.pit.inningsPitched}IP · ${topPitcher.pit.strikeOuts}K · ${topPitcher.pit.earnedRuns}ER`,
      accent: false,
    },
    {
      icon: '😬',
      title: 'LOB',
      value: `${awayLOB} / ${homeLOB}`,
      sub: `${awayAbbr} / ${homeAbbr} Left on Base`,
      accent: false,
    },
  ].filter(Boolean);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {cards.map((card, i) => (
        <HighlightCard key={i} {...card} />
      ))}
    </div>
  );
};

export default GameHighlights;